import { LIST_ITEM_ATTRS } from '@root/helpers/editor';
import { type CommandProps, isMarkActive } from '@tiptap/core';
import ListItemExtension from '@tiptap/extension-list-item';
import type { Node } from '@tiptap/pm/model';
import type { Selection, SelectionRange, Transaction } from '@tiptap/pm/state';

import { selectionCoversTextblock } from './helpers';

type MarkerAttribute = keyof typeof LIST_ITEM_ATTRS;
type MarkerFlag = 'markerBold' | 'markerItalic' | 'markerUnderline';

// A bullet is a drawn disc: colour and size reach it, weight, slant and underline don't
const NOT_ON_BULLETS: MarkerAttribute[] = ['markerBold', 'markerItalic', 'markerUnderline'];

// Only the line's text gates the marker: a datapoint chip at either edge
// may be left out of the selection.
const selectionCoversLineText = (range: SelectionRange, line: Node, pos: number): boolean => {
  let textStart = -1;
  let textEnd = -1;
  line.descendants((child, offset) => {
    if (!child.isText) return;
    if (textStart < 0) textStart = offset;
    textEnd = offset + child.nodeSize;
  });
  if (textStart < 0) return selectionCoversTextblock(range, line, pos);
  return range.$from.pos <= pos + 1 + textStart && range.$to.pos >= pos + 1 + textEnd;
};

// Format a listItem's marker only when the command covers the item's
// entire OWN LINE: its first paragraph, the line the marker renders
// against. Later paragraphs and nested sub-lists never gate the marker.
// Partial selections leave the marker alone, which makes the attribute
// sticky across later typing/pasting.
export const setMarkerOnFullySelectedItems = (
  tr: Transaction,
  selection: Selection,
  attribute: MarkerAttribute,
  value: string | boolean | null,
): void => {
  const listItemType = tr.doc.type.schema.nodes.listItem;
  const { storedMarks } = tr;

  selection.ranges.forEach((range) => {
    tr.doc.nodesBetween(range.$from.pos, range.$to.pos, (node, pos, parent) => {
      if (node.type !== listItemType) return undefined;
      if (NOT_ON_BULLETS.includes(attribute) && parent?.type.name === 'bulletList') return undefined;

      if (selectionCoversLineText(range, node.child(0), pos + 1) && node.attrs[attribute] !== value) {
        tr.setNodeAttribute(pos, attribute, value);
      }
      return undefined;
    });
  });
  tr.setStoredMarks(storedMarks);
};

export const toggleMarkWithMarker =
  (markName: string, markerFlag: MarkerFlag) =>
  () =>
  ({ chain, tr, state, dispatch }: CommandProps) => {
    const { selection } = state;
    const markerValue = !isMarkActive(state, markName);
    const applied = chain().toggleMark(markName).run();
    if (dispatch) setMarkerOnFullySelectedItems(tr, selection, markerFlag, markerValue);
    return applied;
  };

const markerFlag = (name: MarkerFlag) => ({
  default: false,
  parseHTML: (element: HTMLElement) => element.hasAttribute(LIST_ITEM_ATTRS[name]),
  renderHTML: (attributes: Record<string, unknown>) => (attributes[name] ? { [LIST_ITEM_ATTRS[name]]: '' } : {}),
});

// Read via a CSS custom property so the value reaches the marker
// pseudo-element without cascading into the item's text.
const markerValue = (name: 'markerSize' | 'markerColor', cssVariable: string) => ({
  default: null,
  parseHTML: (element: HTMLElement) => element.getAttribute(LIST_ITEM_ATTRS[name]),
  renderHTML: (attributes: Record<string, unknown>) =>
    attributes[name] ? { [LIST_ITEM_ATTRS[name]]: attributes[name], style: `${cssVariable}: ${attributes[name]}` } : {},
});

export const ListItem = ListItemExtension.extend({
  addKeyboardShortcuts() {
    return { ...this.parent?.(), Tab: () => false, 'Shift-Tab': () => false };
  },
  addAttributes() {
    return {
      ...this.parent?.(),
      markerSize: markerValue('markerSize', '--marker-size'),
      markerColor: markerValue('markerColor', '--marker-color'),
      markerBold: markerFlag('markerBold'),
      markerItalic: markerFlag('markerItalic'),
      markerUnderline: markerFlag('markerUnderline'),
      style: {
        parseHTML: () => {
          return '';
        },
      },
    };
  },
});
